import React, { useCallback, useEffect, useState } from "react";
import { LANGUAGE_SV, QUERY_AND_SESSION_KEYS, ROUTES, SESSION_STORAGE_KEY_LANGUAGE } from "~/constants";

import DigitalReturnApp from "./digitalReturns/DigitalReturnApp";
import OrderStatusApp from "./orderStatus/OrderStatusApp";
import PackingInstructionsApp from "./packingInstructions/PackingInstructionsApp";
import { AlertPopupWrapper } from "~/components/alerts/Error";
import CookieComponent from "./appUtils/CookieComponent";
import { cookiesIsDisabled, getDefaultLanguage, getEnabledLanguages, getSupportEmail, isLanguageCodeEnabledOnBrand, normalizeLocaleToLanguageCode } from "~/cores/brandDataCore";
import { BROWSER_STORAGE_TYPES } from "rylak-common/src/hooks/useBrowserStateOld.jsx";
import useBrowserStateWithCookieCheck from "../../hooks/useBrowserStateWithCookieCheck";
import { isInMaintenance } from "~/cores/appCore";
import MaintenancePage from "../../pages/maintenance.page";
import { serviceHasError } from "rylak-common";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";
import { IFRAME_ACTIONS } from "rylak-common/src/constants";
import IframeModalApp from "./IframeModalApp/IframeModalApp";
import useGetLocaleText from "~/hooks/useGetLocaleText";

// const allTextsKeys = getDeepKeys(ALL_TEXTS, {endIterationFn: (obj, key) => !obj[key]?.[LANGUAGE_EN]});

function isInIframeFn() {
    if (typeof window !== "undefined") {
        return window.self !== window.top;
    }
    return false;
}

export function AppEngine(props) {
    const { Page, brandName, forcePhoneView, forceIframe, forceModalIframeActive, brandData, router, system, languageOverride, onLanguageChange } = props;

    const [iframeQuery] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.IFRAME,
    });

    const [globalIframeModalActive] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.MODAL_IFRAME_ACTIVE,
    });

    const isInIframe = forceIframe || (iframeQuery === true || iframeQuery === false ? iframeQuery : isInIframeFn());

    console.log(process.env.NEXT_PUBLIC_TURNR_ENV);

    const isIframeModalApp = router.pathname.startsWith(ROUTES.IFRAME_MODAL_APP.pathname);
    const isPackingInstructionsApp = !isIframeModalApp && router.pathname.startsWith(ROUTES.PACKING_INSTRUCTIONS.pathname);
    const isOrderStatusApp = (!isIframeModalApp && !isPackingInstructionsApp && router.pathname.startsWith(ROUTES.STATUS.pathname)) || router.pathname.startsWith(ROUTES.STATUS_TRACKING_HELP.pathname);
    const isDigitalReturnApp = !isIframeModalApp && !isPackingInstructionsApp && !isOrderStatusApp && router.pathname.startsWith(ROUTES.MAIN_BRAND_PATH.pathname);

    const [appSize, setAppSize] = useState(null);
    const [modalHeight, setModalHeight] = useState(0);

    function updateAppSize() {
        setAppSize({
            width: window?.document?.body?.offsetWidth,
            height: window?.document?.body?.offsetHeight,
        });
    }

    useEffect(() => {
        if (isInIframe) {
            window.document.body.style.height = "auto";
        }
    }, [isInIframe]);

    useEffect(() => {
        const observer = new ResizeObserver(updateAppSize);
        const observeElement = window?.document?.body;
        observer.observe(observeElement);
        return () => observer.unobserve(observeElement);
    }, []);

    useEffect(() => {
        const postHeight = Math.max(appSize?.height, modalHeight);
        if (postHeight && !isIframeModalApp) {
            window.parent.postMessage(
                {
                    action: IFRAME_ACTIONS.APP_SIZE_CHANGE,
                    width: appSize?.width,
                    height: postHeight,
                },
                "*",
            );

            // Deprecated
            window.parent.postMessage(
                {
                    action: IFRAME_ACTIONS.APP_SIZE_CHANGE_OLD,
                    width: appSize?.width,
                    height: postHeight,
                },
                "*",
            );
        }
    }, [appSize, modalHeight, router, isIframeModalApp]);

    useEffect(() => {
        const pageData = Object.values(ROUTES).find((pageData) => pageData.pathname === router.pathname);
        if (pageData && !isIframeModalApp) {
            window.parent.postMessage(
                {
                    action: IFRAME_ACTIONS.PAGE_CHANGE,
                    page: pageData.iframeEventPageName,
                    test: "test",
                },
                "*",
            );
        }
    }, [router.pathname, isIframeModalApp]);

    const [language, setLanguage] = useBrowserStateWithCookieCheck({
        key: SESSION_STORAGE_KEY_LANGUAGE,
        defaultValue: getDefaultLanguage(props),
        formatFn: (languageCode) => normalizeLocaleToLanguageCode(languageCode, getEnabledLanguages({ brandData })),
        approvedFn: (languageCode) => isLanguageCodeEnabledOnBrand({ brandData }, normalizeLocaleToLanguageCode(languageCode, getEnabledLanguages({ brandData }))),
        browserStorageType: BROWSER_STORAGE_TYPES.SESSION,
        initWithDefaultValue: true,
        includeQuery: true,
        shouldUpdateQuery: false,
        isInIframe,
    });

    useEffect(() => {
        if (language && !isIframeModalApp) {
            window.parent.postMessage(
                {
                    action: IFRAME_ACTIONS.TO_MODAL_IFRAME_LANGUAGE_CHANGED,
                    relayToOtherIframe: true,
                    data: {
                        language: language,
                    },
                },
                "*",
            );
        }
    }, [language]);

    const [initialLanguageQuery] = useQueryState({ key: SESSION_STORAGE_KEY_LANGUAGE, shouldUpdateQuery: false });

    const [alerts, setAlerts] = useState([]);

    const setLanguageLocally = useCallback((languageToSet) => {
        setLanguage(languageToSet);
    }, []);

    useEffect(() => {
        languageOverride && setLanguageLocally(languageOverride);
    }, [languageOverride]);

    const listenerFunction = useCallback(
        (e) => {
            const action = e.data?.action || e.data?.messageType;
            if (action === IFRAME_ACTIONS.SET_LANGUAGE_CODE || action === IFRAME_ACTIONS.SET_LANGUAGE_CODE_OLD) {
                const normalized = normalizeLocaleToLanguageCode(e.data?.data?.languageCode, getEnabledLanguages({ brandData }));
                if (isLanguageCodeEnabledOnBrand({ brandData }, normalized)) {
                    setLanguage(normalized);
                } else {
                    setLanguage(getDefaultLanguage({ brandData }));
                }
            }
        },
        [brandData],
    );

    useEffect(() => {
        window.addEventListener("message", listenerFunction);
        return () => {
            window.removeEventListener("message", listenerFunction);
        };
    }, [listenerFunction]);

    useEffect(() => {
        if (language === "SE") {
            setLanguageLocally(LANGUAGE_SV);
            return;
        }
        const enabledLanguages = getEnabledLanguages({ brandData });
        if (!language || !enabledLanguages.includes(language)) {
            // Safety if faulty language been set in cache
            setLanguageLocally(getDefaultLanguage({ brandData }));
            return;
        }
        onLanguageChange && onLanguageChange(language);
    }, [language, brandData]);

    const getLocaleText = useGetLocaleText({ language, shopTextOverrides: brandData?.shopTextOverrides, supportEmail: getSupportEmail({ brandData }) });

    const addAlert = useCallback(
        ({ title, content }) => {
            const newAlerts = alerts.concat([{ title, content }]);
            setAlerts(newAlerts);
        },
        [alerts],
    );

    const sharedAppProps = {
        ...props,
        system: { ...system, getLocaleText },
        addAlert,
        brandData,
        isInIframe,
        getLocaleText,
        language,
        setModalHeight,
        globalIframeModalActive: globalIframeModalActive || forceModalIframeActive,
        appInitiatedWithQueryLanguage: !!initialLanguageQuery,
        setLanguage: setLanguageLocally,
        appWidth: appSize?.width,
        appHeight: appSize?.height,
        forcePhoneView,
    };

    if (serviceHasError(sharedAppProps?.initServiceData)) {
        console.error(sharedAppProps?.initServiceData);
    }

    if (isInMaintenance(sharedAppProps)) {
        return <MaintenancePage />;
    }

    return brandName ? (
        <>
            {!cookiesIsDisabled(sharedAppProps) && <CookieComponent sharedAppProps={sharedAppProps} />}
            <AlertPopupWrapper {...sharedAppProps} alerts={alerts} />
            {isPackingInstructionsApp && <PackingInstructionsApp {...sharedAppProps} sharedAppProps={sharedAppProps} />}
            {isOrderStatusApp && <OrderStatusApp {...sharedAppProps} sharedAppProps={sharedAppProps} />}
            {isDigitalReturnApp && <DigitalReturnApp {...sharedAppProps} sharedAppProps={sharedAppProps} />}
            {isIframeModalApp && <IframeModalApp {...sharedAppProps} sharedAppProps={sharedAppProps} />}
        </>
    ) : (
        <>
            <AlertPopupWrapper {...sharedAppProps} alerts={alerts} />
            <Page {...sharedAppProps} appProps={sharedAppProps} />
        </>
    );
}

export default AppEngine;
